import Component from '../core/Component'
import { loadComponents, removeComponents } from '../core/componentUtils'
import throttle from 'lodash/throttle'
import support from '../utils/BrowserSupport'
import scrollToElement from '../services/Viewport/scrollToElement'

import $ from 'jquery';

const States = {
    OPEN: 'is-open',
    ACTIVE: 'is-active',
    CLOSABLE: 'is-closable'
}

export default class BlockReveal extends Component {
    constructor(element) {
        super(element)

        this.request = null

        this.options = {
            selector: this.element.dataset.blockRevealSelector
        }

        this.cache = {}

        this.current = {
            link: null,
            container: null
        }

        this.ref = {
            items: [],
            links: [],
            arrow: null
        }

        this.canCancelScroll = false
        this.cancelScroll = false

        this.handleResize = throttle(::this.handleResize, 200)
    }

    prepare() {
        this.element.addEventListener('click', ::this.handleClick)
        this.element.addEventListener('change', ::this.handleChange)
        this.tagName = this.ref.items[0].tagName

        this.current = {
            container: null,
            content: null,
            item: null,
            link: null
        }

        window.addEventListener('resize', this.handleResize)

        document.addEventListener('scroll', () => {
            if (this.canCancelScroll & !this.cancelScroll) {
                this.cancelScroll = true
            }
        })
    }

    destroy() {
        window.removeEventListener('resize', this.handleResize)
    }

    handleChange(event) {
        const item = event.target.closest('[data-ref="BlockReveal:item"]')

        if (item !== this.current.item) {
            this.hide()
        }
    }

    handleClick(event) {
        const target = event.target.closest('[data-ref="BlockReveal:link"]')

        if (!target) {
            return
        }

        if (!this.options.selector) {
            throw Error('Selector required')
        }

        if (target.tagName.toLowerCase() === 'a') {
            event.preventDefault()
            this.show(target.getAttribute('href'))
        } else {
            this.show(event.target.closest('[data-ref="BlockReveal:item"]').dataset.url)
        }
    }

    handleResize(event) {
        this.resize()
    }

    getItem(link) {
        return this.element.querySelector(`[href="${link}"], [data-url="${link}"]`).closest('[data-ref="BlockReveal:item"]')
    }

    createContainer() {
        const container = document.createElement(this.tagName)
        container.className = 'BlockReveal'

        container.addEventListener(support.transitionEnd, () => {
            if (parseInt(container.style.height) > 0 && this.current.item && !this.cancelScroll) {
                scrollToElement(this.current.item)
            }
        })

        const arrow = document.createElement('div')
        arrow.className = 'BlockReveal-arrow'
        arrow.dataset.ref = 'BlockReveal:arrow'
        container.appendChild(arrow)

        return container
    }

    createContent(content) {
        const buffer = document.createElement('div')
        buffer.className = 'BlockReveal-content'
        buffer.appendChild(content)
        return buffer
    }

    getContent(link) {
        const buffer = document.createElement('div')
        buffer.innerHTML = this.cache[link]
        return buffer.children[0]
    }

    load(link) {
        if (this.current.container) {
            this.current.container.classList.add('is-loading')
        }
        this.request = $.ajax({
            url: link,
            dataType: 'html',
            success: response => {
                const buffer = document.createElement('div')
                buffer.innerHTML = response

                const content = buffer.querySelector(this.options.selector)
                this.cache[link] = this.createContent(content).outerHTML
                this.show(link)
            },
            complete: () => {
                if (this.current.container) {
                    this.current.container.classList.remove('is-loading')
                }
            }
        })
    }

    show(link, scroll = false) {
        if (this.current.link === link) {
            this.hide()
            return
        }

        if (this.request) {
            this.request.abort()
        }

        if (!(link in this.cache)) {
            this.load(link)
            return
        }

        const item = this.getItem(link)
        const content = this.getContent(link)

        if (this.current.item && this.current.item.getBoundingClientRect().top === item.getBoundingClientRect().top) {
            this.canCancelScroll = true
            this.current.item.classList.remove(States.CLOSABLE)
            this.current.container.appendChild(content)

            const height = Math.ceil(content.getBoundingClientRect().height + parseInt(window.getComputedStyle(this.ref.items[0]).marginBottom))

            const oldContent = this.current.content

            oldContent.addEventListener(support.transitionEnd, () => {
                removeComponents(oldContent)
                oldContent.parentNode.removeChild(oldContent)
            })

            oldContent.classList.remove(States.ACTIVE)

            this.current.container.classList.add(States.ACTIVE)
            this.current.container.style.height = `${height}px`

        } else {
            this.hide()

            this.canCancelScroll = true

            const container = this.createContainer()
            const last = this.getLastItemInRow(item)

            container.appendChild(content)
            last.parentNode.insertBefore(container, last.nextSibling)
            container.classList.add(States.ACTIVE)

            const height = Math.ceil(container.scrollHeight + parseInt(window.getComputedStyle(this.ref.items[0]).marginBottom))
            container.style.height = `${height}px`

            this.current.container = container
        }


        this.current.content = content
        this.current.item = item
        this.current.link = link

        this.current.item.classList.add(States.CLOSABLE)
        this.updateArrowPosition(this.current.item)
        this.current.content.classList.add(States.ACTIVE)

        loadComponents(this.current.content)

        if (scroll) {
            scrollToElement(this.current.item)
        }
    }

    hide() {
        this.canCancelScroll = false
        this.cancelScroll = false

        if (this.current.container) {
            const { container, content } = this.current
            container.addEventListener(support.transitionEnd, () => {
                removeComponents(content)
                content.parentNode.removeChild(content)
                content.classList.remove(States.ACTIVE)
                container.parentNode.removeChild(container)
            })

            container.style.height = 0

            this.current.container = null
            this.current.content = null
            this.current.item.classList.remove(States.CLOSABLE)
            this.current.item = null
            this.current.link = null

        }
    }

    resize() {
        if (this.current.container) {

            const height = Math.ceil(this.current.content.getBoundingClientRect().height + parseInt(window.getComputedStyle(this.ref.items[0]).marginBottom))
            this.current.container.style.height = `${height}px`
        }
    }

    getLastItemInRow(origin) {
        const offset = origin.getBoundingClientRect().top
        const row = this.ref.items.filter(item => item.getBoundingClientRect().top === offset)
        return row[row.length - 1]
    }

    updateArrowPosition(item) {
        const arrow = this.current.container.querySelector('[data-ref="BlockReveal:arrow"]')
        const itemBox = item.getBoundingClientRect()
        const containerBox = this.current.container.getBoundingClientRect()

        arrow.style.left = `${itemBox.left + itemBox.width/2 - containerBox.left}px`
    }

}
